/**
 * Feature registry.
 *
 * Collects routes, reducers, middleware, translations, and page data loaders
 * from enabled features. Each feature exports what it needs through its index.js.
 *
 * When adding a new feature:
 * 1. Create src/features/{featureName}/index.js
 * 2. Add the feature key to config.js
 * 3. Add the import block below (use camelCase matching config key)
 */
import { features } from './config';

let featureRoutes = [];
let featureReducers = {};
let featureMiddleware = [];
let featureTranslations = {};
let featurePageDataLoading = {};

// @feature:passwordIndicator
if (features.passwordIndicator) {
  const mod = require('./passwordIndicator');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:passwordIndicator:end

// @feature:reorderPhotos
// if (features.reorderPhotos) {
//   const mod = require('./reorderPhotos');
//   Object.assign(featureTranslations, mod.translations || {});
// }
// @feature:reorderPhotos:end

// @feature:previewDraft
if (features.previewDraft) {
  const mod = require('./previewDraft');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:previewDraft:end

// @feature:averageReviews
if (features.averageReviews) {
  const mod = require('./averageReviews');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:averageReviews:end

// @feature:addOns
if (features.addOns) {
  const mod = require('./addOns');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:addOns:end

// @feature:favourites
if (features.favourites) {
  const mod = require('./favourites');
  Object.assign(featureTranslations, mod.translations || {});
  // RTK Query API reducers and middleware
  featureReducers[mod.UserApi.reducerPath] = mod.UserApi.reducer;
  featureReducers[mod.FavouriteListingsApi.reducerPath] = mod.FavouriteListingsApi.reducer;
  featureMiddleware = featureMiddleware.concat([
    mod.UserApi.middleware,
    mod.FavouriteListingsApi.middleware,
  ]);
}
// @feature:favourites:end

// @feature:attachFiles
if (features.attachFiles) {
  const mod = require('./attachFiles');
  Object.assign(featureTranslations, mod.translations || {});
  // RTK Query API reducers and middleware
  featureReducers[mod.AwsS3TransactionMessagesApi.reducerPath] =
    mod.AwsS3TransactionMessagesApi.reducer;
  featureReducers[mod.TransactionApi.reducerPath] = mod.TransactionApi.reducer;
  featureMiddleware = featureMiddleware.concat([
    mod.AwsS3TransactionMessagesApi.middleware,
    mod.TransactionApi.middleware,
  ]);
}
// @feature:attachFiles:end

// @feature:cart
if (features.cart) {
  const mod = require('./cart');
  Object.assign(featureTranslations, mod.translations || {});
  featureReducers[mod.CartApi.reducerPath] = mod.CartApi.reducer;
  featureMiddleware = featureMiddleware.concat([mod.CartApi.middleware]);
}
// @feature:cart:end

// @feature:vatValidation
if (features.vatValidation) {
  const mod = require('./vatValidation');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:vatValidation:end

// @feature:socialShare
if (features.socialShare) {
  const mod = require('./socialShare');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:socialShare:end

// @feature:profilePhotoCrop
if (features.profilePhotoCrop) {
  const mod = require('./profilePhotoCrop');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:profilePhotoCrop:end

// @feature:videoPlayer
if (features.videoPlayer) {
  const mod = require('./videoPlayer');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:videoPlayer:end

// @feature:smtp
if (features.smtp) {
  const mod = require('./smtp');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:smtp:end

// @feature:captchaRecaptcha
if (features.captchaRecaptcha) {
  const mod = require('./captchaRecaptcha');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:captchaRecaptcha:end

// @feature:captchaTurnstile
if (features.captchaTurnstile) {
  const mod = require('./captchaTurnstile');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:captchaTurnstile:end

// @feature:cookieConsent
if (features.cookieConsent) {
  const mod = require('./cookieConsent');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:cookieConsent:end

// @feature:cronJobs
// (server-only feature, no client translations)
// @feature:cronJobs:end

// @feature:profileAddresses
if (features.profileAddresses) {
  const mod = require('./profileAddresses');
  Object.assign(featureTranslations, mod.translations || {});
  featureReducers[mod.ProfileAddressesApi.reducerPath] = mod.ProfileAddressesApi.reducer;
  featureMiddleware = featureMiddleware.concat([mod.ProfileAddressesApi.middleware]);
  featureRoutes = featureRoutes.concat(mod.routes || []);
}
// @feature:profileAddresses:end

// @feature:shippingDelivery
if (features.shippingDelivery) {
  const mod = require('./shippingDelivery');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:shippingDelivery:end

// @feature:listingsApi
if (features.listingsApi) {
  const mod = require('./listingsApi');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:listingsApi:end

// @feature:mailchimp
if (features.mailchimp) {
  const mod = require('./mailchimp');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:mailchimp:end

// @feature:twilioSms
// (server-only feature, no client translations)
// @feature:twilioSms:end

// @feature:pdfInvoice
if (features.pdfInvoice) {
  const mod = require('./pdfInvoice');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:pdfInvoice:end

// @feature:voucherCodes
if (features.voucherCodes) {
  const mod = require('./voucherCodes');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:voucherCodes:end

// @feature:listingFiles
if (features.listingFiles) {
  const mod = require('./listingFiles');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:listingFiles:end

// @feature:contactForm
if (features.contactForm) {
  const mod = require('./contactForm');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:contactForm:end

// @feature:sendgrid
// (server-only feature — no client translations)
// @feature:sendgrid:end

// @feature:vacationMode
if (features.vacationMode) {
  const mod = require('./vacationMode');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:vacationMode:end

// @feature:agoraVideo
if (features.agoraVideo) {
  const mod = require('./agoraVideo');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:agoraVideo:end

// @feature:transactionMetadata
if (features.transactionMetadata) {
  const mod = require('./transactionMetadata');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:transactionMetadata:end

// @feature:intercom
if (features.intercom) {
  const mod = require('./intercom');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:intercom:end

// @feature:notificationPreferences
if (features.notificationPreferences) {
  const mod = require('./notificationPreferences');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:notificationPreferences:end

// @feature:stripeSubscriptions
if (features.stripeSubscriptions) {
  const mod = require('./stripeSubscriptions');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:stripeSubscriptions:end

// @feature:paypal
if (features.paypal) {
  const mod = require('./paypal');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:paypal:end

// @feature:adminApi
if (features.adminApi) {
  const mod = require('./adminApi');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:adminApi:end

// @feature:slackNotifications
if (features.slackNotifications) {
  const mod = require('./slackNotifications');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:slackNotifications:end

// @feature:gdprDeleteAccount
if (features.gdprDeleteAccount) {
  const mod = require('./gdprDeleteAccount');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:gdprDeleteAccount:end

// @feature:webhookReceiver
if (features.webhookReceiver) {
  const mod = require('./webhookReceiver');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:webhookReceiver:end

// @feature:gtm
if (features.gtm) {
  const mod = require('./gtm');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:gtm:end

// @feature:listingBadges
if (features.listingBadges) {
  const mod = require('./listingBadges');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:listingBadges:end

// @feature:whatsapp
if (features.whatsapp) {
  const mod = require('./whatsapp');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:whatsapp:end

// @feature:emailTemplates
if (features.emailTemplates) {
  const mod = require('./emailTemplates');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:emailTemplates:end

// @feature:announcementBanner
if (features.announcementBanner) {
  const mod = require('./announcementBanner');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:announcementBanner:end

// @feature:listingStats
if (features.listingStats) {
  const mod = require('./listingStats');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:listingStats:end

// @feature:waitlist
if (features.waitlist) {
  const mod = require('./waitlist');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:waitlist:end

// @feature:userTiers
if (features.userTiers) {
  const mod = require('./userTiers');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:userTiers:end

// @feature:downloadableContent
if (features.downloadableContent) {
  const mod = require('./downloadableContent');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:downloadableContent:end

// @feature:userBlocking
if (features.userBlocking) {
  const mod = require('./userBlocking');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:userBlocking:end

// @feature:savedSearches
if (features.savedSearches) {
  const mod = require('./savedSearches');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:savedSearches:end

// @feature:sitemapXml
if (features.sitemapXml) {
  const mod = require('./sitemapXml');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:sitemapXml:end

// @feature:priceRules
if (features.priceRules) {
  const mod = require('./priceRules');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:priceRules:end

// @feature:ipGeoDetect
if (features.ipGeoDetect) {
  const mod = require('./ipGeoDetect');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:ipGeoDetect:end

// @feature:autoTranslation
if (features.autoTranslation) {
  const mod = require('./autoTranslation');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:autoTranslation:end

// @feature:customWebhooks
if (features.customWebhooks) {
  const mod = require('./customWebhooks');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:customWebhooks:end

// @feature:rateLimiting
if (features.rateLimiting) {
  const mod = require('./rateLimiting');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:rateLimiting:end

// @feature:referralTracking
if (features.referralTracking) {
  const mod = require('./referralTracking');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:referralTracking:end

// @feature:exportData
if (features.exportData) {
  const mod = require('./exportData');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:exportData:end

// @feature:contentReports
if (features.contentReports) {
  const mod = require('./contentReports');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:contentReports:end

// @feature:multiCurrency
if (features.multiCurrency) {
  const mod = require('./multiCurrency');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:multiCurrency:end

// @feature:featuredListings
if (features.featuredListings) {
  const mod = require('./featuredListings');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:featuredListings:end

// @feature:userInvites
if (features.userInvites) {
  const mod = require('./userInvites');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:userInvites:end

// @feature:reviewResponse
if (features.reviewResponse) {
  const mod = require('./reviewResponse');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:reviewResponse:end

// @feature:qrCode
if (features.qrCode) {
  const mod = require('./qrCode');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:qrCode:end

// @feature:bookingReminder
if (features.bookingReminder) {
  const mod = require('./bookingReminder');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:bookingReminder:end

// @feature:imageCompression
if (features.imageCompression) {
  const mod = require('./imageCompression');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:imageCompression:end

// @feature:socialProof
if (features.socialProof) {
  const mod = require('./socialProof');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:socialProof:end

// @feature:scheduledPublish
if (features.scheduledPublish) {
  const mod = require('./scheduledPublish');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:scheduledPublish:end

// @feature:loginAttempts
if (features.loginAttempts) {
  const mod = require('./loginAttempts');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:loginAttempts:end

// @feature:listingDuplicator
if (features.listingDuplicator) {
  const mod = require('./listingDuplicator');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:listingDuplicator:end

// @feature:userMilestones
if (features.userMilestones) {
  const mod = require('./userMilestones');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:userMilestones:end

// @feature:twoFactorAuth
if (features.twoFactorAuth) {
  const mod = require('./twoFactorAuth');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:twoFactorAuth:end

// @feature:flashSales
if (features.flashSales) {
  const mod = require('./flashSales');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:flashSales:end

// @feature:abandonedCart
if (features.abandonedCart) {
  const mod = require('./abandonedCart');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:abandonedCart:end

// @feature:affiliateLinks
if (features.affiliateLinks) {
  const mod = require('./affiliateLinks');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:affiliateLinks:end

// @feature:inAppNotifications
if (features.inAppNotifications) {
  const mod = require('./inAppNotifications');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:inAppNotifications:end

// @feature:liveSearch
if (features.liveSearch) {
  const mod = require('./liveSearch');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:liveSearch:end

// @feature:darkMode
if (features.darkMode) {
  const mod = require('./darkMode');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:darkMode:end

// @feature:listingComparisons
if (features.listingComparisons) {
  const mod = require('./listingComparisons');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:listingComparisons:end

// @feature:multiLanguage
if (features.multiLanguage) {
  const mod = require('./multiLanguage');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:multiLanguage:end

// @feature:onboardingChecklist
if (features.onboardingChecklist) {
  const mod = require('./onboardingChecklist');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:onboardingChecklist:end

// @feature:maintenanceMode
if (features.maintenanceMode) {
  const mod = require('./maintenanceMode');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:maintenanceMode:end

// @feature:bookingNotes
if (features.bookingNotes) {
  const mod = require('./bookingNotes');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:bookingNotes:end

// @feature:activityLog
if (features.activityLog) {
  const mod = require('./activityLog');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:activityLog:end

// @feature:ageVerification
if (features.ageVerification) {
  const mod = require('./ageVerification');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:ageVerification:end

// @feature:listingEndDate
if (features.listingEndDate) {
  const mod = require('./listingEndDate');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:listingEndDate:end

// @feature:helpCenter
if (features.helpCenter) {
  const mod = require('./helpCenter');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:helpCenter:end

// @feature:customPages
if (features.customPages) {
  const mod = require('./customPages');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:customPages:end

// @feature:listingAlerts
if (features.listingAlerts) {
  const mod = require('./listingAlerts');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:listingAlerts:end

// @feature:bulkUpload
if (features.bulkUpload) {
  const mod = require('./bulkUpload');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:bulkUpload:end

// @feature:sellerDashboard
if (features.sellerDashboard) {
  const mod = require('./sellerDashboard');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:sellerDashboard:end

// @feature:listingApproval
if (features.listingApproval) {
  const mod = require('./listingApproval');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:listingApproval:end

// @feature:commissionOverride
if (features.commissionOverride) {
  const mod = require('./commissionOverride');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:commissionOverride:end

// @feature:guestInquiry
if (features.guestInquiry) {
  const mod = require('./guestInquiry');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:guestInquiry:end

// @feature:tipping
if (features.tipping) {
  const mod = require('./tipping');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:tipping:end

// @feature:bookingReminders
if (features.bookingReminders) {
  const mod = require('./bookingReminders');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:bookingReminders:end

// @feature:reviewRequest
if (features.reviewRequest) {
  const mod = require('./reviewRequest');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:reviewRequest:end

// @feature:googleCalendar
if (features.googleCalendar) {
  const mod = require('./googleCalendar');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:googleCalendar:end

// @feature:userVerification
if (features.userVerification) {
  const mod = require('./userVerification');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:userVerification:end

// @feature:pushNotifications
if (features.pushNotifications) {
  const mod = require('./pushNotifications');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:pushNotifications:end

// @feature:priceNegotiation
if (features.priceNegotiation) {
  const mod = require('./priceNegotiation');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:priceNegotiation:end

// @feature:dynamicPricing
if (features.dynamicPricing) {
  const mod = require('./dynamicPricing');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:dynamicPricing:end

// @feature:depositPayment
if (features.depositPayment) {
  const mod = require('./depositPayment');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:depositPayment:end

// @feature:inventoryAlert
if (features.inventoryAlert) {
  const mod = require('./inventoryAlert');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:inventoryAlert:end

// @feature:loyaltyPoints
if (features.loyaltyPoints) {
  const mod = require('./loyaltyPoints');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:loyaltyPoints:end

// @feature:earlyBirdDiscount
if (features.earlyBirdDiscount) {
  const mod = require('./earlyBirdDiscount');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:earlyBirdDiscount:end

// @feature:recurringBooking
if (features.recurringBooking) {
  const mod = require('./recurringBooking');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:recurringBooking:end

// @feature:passwordlessLogin
if (features.passwordlessLogin) {
  const mod = require('./passwordlessLogin');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:passwordlessLogin:end

// @feature:listingTemplate
if (features.listingTemplate) {
  const mod = require('./listingTemplate');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:listingTemplate:end

// @feature:searchSuggestions
if (features.searchSuggestions) {
  const mod = require('./searchSuggestions');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:searchSuggestions:end

// @feature:userGrouping
if (features.userGrouping) {
  const mod = require('./userGrouping');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:userGrouping:end

// @feature:bookingExtension
if (features.bookingExtension) {
  const mod = require('./bookingExtension');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:bookingExtension:end

// @feature:abandonedSignup
if (features.abandonedSignup) {
  const mod = require('./abandonedSignup');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:abandonedSignup:end

// @feature:lastMinuteDeals
if (features.lastMinuteDeals) {
  const mod = require('./lastMinuteDeals');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:lastMinuteDeals:end

// @feature:bundleDiscount
if (features.bundleDiscount) {
  const mod = require('./bundleDiscount');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:bundleDiscount:end

// @feature:eventBooking
if (features.eventBooking) {
  const mod = require('./eventBooking');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:eventBooking:end

// @feature:taxCalculator
if (features.taxCalculator) {
  const mod = require('./taxCalculator');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:taxCalculator:end

// @feature:membershipGating
if (features.membershipGating) {
  const mod = require('./membershipGating');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:membershipGating:end

// @feature:geoRadius
if (features.geoRadius) {
  const mod = require('./geoRadius');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:geoRadius:end

// @feature:splitPayment
if (features.splitPayment) {
  const mod = require('./splitPayment');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:splitPayment:end

// @feature:wishlistShare
if (features.wishlistShare) {
  const mod = require('./wishlistShare');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:wishlistShare:end

// @feature:bookingCalendar
if (features.bookingCalendar) {
  const mod = require('./bookingCalendar');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:bookingCalendar:end

// @feature:autoApprove
if (features.autoApprove) {
  const mod = require('./autoApprove');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:autoApprove:end

// @feature:returnPolicy
if (features.returnPolicy) {
  const mod = require('./returnPolicy');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:returnPolicy:end

// @feature:multiVariant
if (features.multiVariant) {
  const mod = require('./multiVariant');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:multiVariant:end

// @feature:floorPlan
if (features.floorPlan) {
  const mod = require('./floorPlan');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:floorPlan:end

// @feature:priceHistory
if (features.priceHistory) {
  const mod = require('./priceHistory');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:priceHistory:end

// @feature:bulkMessaging
if (features.bulkMessaging) {
  const mod = require('./bulkMessaging');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:bulkMessaging:end

// @feature:listingQA
if (features.listingQA) {
  const mod = require('./listingQA');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:listingQA:end

// @feature:groupBooking
if (features.groupBooking) {
  const mod = require('./groupBooking');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:groupBooking:end

// @feature:smartPricing
if (features.smartPricing) {
  const mod = require('./smartPricing');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:smartPricing:end

// @feature:subscriptionBox
if (features.subscriptionBox) {
  const mod = require('./subscriptionBox');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:subscriptionBox:end

// @feature:adminBadge
if (features.adminBadge) {
  const mod = require('./adminBadge');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:adminBadge:end

// @feature:stockNotify
if (features.stockNotify) {
  const mod = require('./stockNotify');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:stockNotify:end

// @feature:earningsCalculator
if (features.earningsCalculator) {
  const mod = require('./earningsCalculator');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:earningsCalculator:end

// @feature:disputeResolution
if (features.disputeResolution) {
  const mod = require('./disputeResolution');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:disputeResolution:end

// @feature:chatTranslate
if (features.chatTranslate) {
  const mod = require('./chatTranslate');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:chatTranslate:end

// @feature:customFields
if (features.customFields) {
  const mod = require('./customFields');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:customFields:end

// @feature:auctionBidding
if (features.auctionBidding) {
  const mod = require('./auctionBidding');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:auctionBidding:end

// @feature:leadCapture
if (features.leadCapture) {
  const mod = require('./leadCapture');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:leadCapture:end

// @feature:npsRating
if (features.npsRating) {
  const mod = require('./npsRating');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:npsRating:end

// @feature:giftCard
if (features.giftCard) {
  const mod = require('./giftCard');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:giftCard:end

// @feature:crossSell
if (features.crossSell) {
  const mod = require('./crossSell');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:crossSell:end

// @feature:bookingInsurance
if (features.bookingInsurance) {
  const mod = require('./bookingInsurance');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:bookingInsurance:end

// @feature:providerCalendar
if (features.providerCalendar) {
  const mod = require('./providerCalendar');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:providerCalendar:end

// @feature:chatbot
if (features.chatbot) {
  const mod = require('./chatbot');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:chatbot:end

// @feature:serviceArea
if (features.serviceArea) {
  const mod = require('./serviceArea');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:serviceArea:end

// @feature:listingCondition
if (features.listingCondition) {
  const mod = require('./listingCondition');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:listingCondition:end

// @feature:listingSpotlight
if (features.listingSpotlight) {
  const mod = require('./listingSpotlight');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:listingSpotlight:end

// @feature:appointmentSlots
if (features.appointmentSlots) {
  const mod = require('./appointmentSlots');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:appointmentSlots:end

// @feature:userSubscription
if (features.userSubscription) {
  const mod = require('./userSubscription');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:userSubscription:end

// @feature:socialConnect
if (features.socialConnect) {
  const mod = require('./socialConnect');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:socialConnect:end

// @feature:proofOfDelivery
if (features.proofOfDelivery) {
  const mod = require('./proofOfDelivery');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:proofOfDelivery:end

// @feature:carbonOffset
if (features.carbonOffset) {
  const mod = require('./carbonOffset');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:carbonOffset:end

// @feature:providerRanking
if (features.providerRanking) {
  const mod = require('./providerRanking');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:providerRanking:end

// @feature:embeddedVideo
if (features.embeddedVideo) {
  const mod = require('./embeddedVideo');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:embeddedVideo:end

// @feature:documentVerification
if (features.documentVerification) {
  const mod = require('./documentVerification');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:documentVerification:end

// @feature:listingWizard
if (features.listingWizard) {
  const mod = require('./listingWizard');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:listingWizard:end

// @feature:bookingBuffer
if (features.bookingBuffer) {
  const mod = require('./bookingBuffer');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:bookingBuffer:end

// @feature:listingAnalytics
if (features.listingAnalytics) {
  const mod = require('./listingAnalytics');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:listingAnalytics:end

// @feature:coHosting
if (features.coHosting) {
  const mod = require('./coHosting');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:coHosting:end

// @feature:minimumBookingPeriod
if (features.minimumBookingPeriod) {
  const mod = require('./minimumBookingPeriod');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:minimumBookingPeriod:end

// @feature:urgencyCountdown
if (features.urgencyCountdown) {
  const mod = require('./urgencyCountdown');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:urgencyCountdown:end

// @feature:priceAlerts
if (features.priceAlerts) {
  const mod = require('./priceAlerts');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:priceAlerts:end

// @feature:reviewHighlights
if (features.reviewHighlights) {
  const mod = require('./reviewHighlights');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:reviewHighlights:end

// @feature:businessHours
if (features.businessHours) {
  const mod = require('./businessHours');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:businessHours:end

// @feature:bulkListingEdit
if (features.bulkListingEdit) {
  const mod = require('./bulkListingEdit');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:bulkListingEdit:end

// @feature:mapView
if (features.mapView) {
  const mod = require('./mapView');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:mapView:end

// @feature:profilePortfolio
if (features.profilePortfolio) {
  const mod = require('./profilePortfolio');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:profilePortfolio:end

// @feature:tierDiscount
if (features.tierDiscount) {
  const mod = require('./tierDiscount');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:tierDiscount:end

// @feature:messagePinning
if (features.messagePinning) {
  const mod = require('./messagePinning');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:messagePinning:end

// @feature:locationSuggester
if (features.locationSuggester) {
  const mod = require('./locationSuggester');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:locationSuggester:end

// @feature:abandonedProposal
if (features.abandonedProposal) {
  const mod = require('./abandonedProposal');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:abandonedProposal:end

// @feature:seasonalPricing
if (features.seasonalPricing) {
  const mod = require('./seasonalPricing');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:seasonalPricing:end

// @feature:quickReply
if (features.quickReply) {
  const mod = require('./quickReply');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:quickReply:end

// @feature:listingCertification
if (features.listingCertification) {
  const mod = require('./listingCertification');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:listingCertification:end

// @feature:viewHistory
if (features.viewHistory) {
  const mod = require('./viewHistory');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:viewHistory:end

// @feature:paymentPlan
if (features.paymentPlan) {
  const mod = require('./paymentPlan');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:paymentPlan:end

// @feature:hostBadge
if (features.hostBadge) {
  const mod = require('./hostBadge');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:hostBadge:end

// @feature:autoResponse
if (features.autoResponse) {
  const mod = require('./autoResponse');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:autoResponse:end

// @feature:searchRadius
if (features.searchRadius) {
  const mod = require('./searchRadius');
  Object.assign(featureTranslations, mod.translations || {});
}
// @feature:searchRadius:end

export {
  featureRoutes,
  featureReducers,
  featureMiddleware,
  featureTranslations,
  featurePageDataLoading,
};
